import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Accessibility,
  ArrowRight,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  HeartPulse,
  Hospital,
  Sparkles,
} from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Reveal, RevealText } from "@project/user/components/motion";
import { useI18n } from "@project/lib/use-i18n";

// Project titles are shared with the About page milestones timeline.
const PROJECT_KEY = "pages.about.milestones.upcoming";
const AUTOPLAY_MS = 4500;

const projects = [
  { key: "vatsalyadham", icon: Building2, catKey: "home.upcoming.catElderCare", hasBody: true },
  { key: "hospital", icon: Hospital, catKey: "home.upcoming.catHospital" },
  { key: "cancerCare", icon: HeartPulse, catKey: "home.upcoming.catCancerCare" },
  { key: "paralysisRehab", icon: Accessibility, catKey: "home.upcoming.catRehab" },
];

export function UpcomingProjectsSection({ className = "" }: { className?: string }) {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);
  const n = projects.length;

  const go = (dir: number) => setActive((a) => (a + dir + n) % n);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => go(1), AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [paused, active]);

  const num = (i: number) =>
    lang === "gu" ? `૦${"૧૨૩૪"[i]}` : String(i + 1).padStart(2, "0");

  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-br from-secondary/50 via-background to-secondary/30 ${className}`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-10 h-80 w-80 rounded-full bg-gold/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-site items-center gap-10 px-4 py-16 md:py-20 lg:grid-cols-[1fr_1.25fr] lg:gap-12">
        {/* ---------- Left: copy ---------- */}
        <Reveal variant="left">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold shadow-sm">
            <Sparkles className="h-3.5 w-3.5" />
            {t("home.upcoming.eyebrow")}
          </span>
          <h2 className="mt-5 font-serif text-4xl font-semibold leading-tight text-foreground md:text-5xl">
            <RevealText delay={150}>
              {t("home.upcoming.headingBefore")}{" "}
              <span className="italic text-gold">
                {t("home.upcoming.headingHighlight")}
              </span>
            </RevealText>
          </h2>
          <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-muted-foreground md:text-base">
            {t("home.upcoming.description")}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground">
              <Calendar className="h-4 w-4 text-gold" />
              {t("home.upcoming.years")}
            </span>
            <span className="font-serif text-lg text-muted-foreground">
              <span className="font-bold text-primary">{num(active)}</span>
              {" / "}
              {num(n - 1)}
            </span>
          </div>

          <Button
            asChild
            size="lg"
            className="btn-lift group mt-8 rounded-full bg-gold text-gold-foreground shadow-md hover:bg-gold/90"
          >
            <Link to="/donate">
              {t("home.upcoming.ctaButton")}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </Reveal>

        {/* ---------- Right: coverflow carousel ---------- */}
        <Reveal variant="morph" delay={200}>
          <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={(e) => {
              touchX.current = e.touches[0].clientX;
              setPaused(true);
            }}
            onTouchEnd={(e) => {
              if (touchX.current !== null) {
                const dx = e.changedTouches[0].clientX - touchX.current;
                if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
              }
              touchX.current = null;
              setPaused(false);
            }}
          >
            <div className="relative h-[340px] overflow-hidden sm:h-[320px]">
              {projects.map((p, i) => {
                // signed distance from the active card, wrapping around
                let d = (i - active + n) % n;
                if (d > n / 2) d -= n;
                const isActive = d === 0;
                const isSide = Math.abs(d) === 1;

                return (
                  <article
                    key={p.key}
                    aria-hidden={!isActive}
                    onClick={() => isSide && setActive(i)}
                    className={`absolute left-1/2 top-1/2 flex min-h-[270px] w-[78%] max-w-[330px] flex-col rounded-2xl border bg-card p-6 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isActive
                        ? "border-gold/50 shadow-2xl shadow-primary/15"
                        : "cursor-pointer border-border shadow-md"
                    }`}
                    style={{
                      transform: `translate(-50%, -50%) translateX(${d * 68}%) scale(${isActive ? 1 : 0.86})`,
                      opacity: isActive ? 1 : isSide ? 0.5 : 0,
                      zIndex: isActive ? 30 : isSide ? 20 : 10,
                      filter: isActive ? "none" : "blur(0.5px)",
                      pointerEvents: isActive || isSide ? "auto" : "none",
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className={`grid h-12 w-12 place-items-center rounded-xl transition-colors duration-500 ${
                          isActive
                            ? "bg-primary text-primary-foreground shadow-md"
                            : "bg-secondary text-primary"
                        }`}
                      >
                        <p.icon className="h-6 w-6" />
                      </div>
                      <span className="font-serif text-3xl font-bold leading-none text-gold/60">
                        {num(i)}
                      </span>
                    </div>

                    <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">
                      {t(p.catKey)}
                    </p>
                    <h3 className="mt-1.5 !font-sans !text-xl font-bold !leading-snug !text-foreground">
                      {t(`${PROJECT_KEY}.${p.key}.title`)}
                    </h3>
                    {p.hasBody && (
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {t(`${PROJECT_KEY}.${p.key}.body`)}
                      </p>
                    )}

                    <div className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-4 text-xs font-semibold">
                      <span className="inline-flex items-center gap-1.5 text-primary">
                        <Calendar className="h-3.5 w-3.5 text-gold" />
                        {t("home.upcoming.years")}
                      </span>
                      <span className="rounded-full bg-gold/15 px-2.5 py-0.5 uppercase tracking-[0.14em] text-gold">
                        {t("home.upcoming.comingSoon")}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* arrows */}
            <button
              type="button"
              aria-label="Previous project"
              onClick={() => go(-1)}
              className="absolute left-0 top-1/2 z-40 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-border bg-card text-foreground shadow-md transition hover:scale-105 hover:border-gold hover:text-gold"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next project"
              onClick={() => go(1)}
              className="absolute right-0 top-1/2 z-40 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-border bg-card text-foreground shadow-md transition hover:scale-105 hover:border-gold hover:text-gold"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* dots */}
            <div className="mt-4 flex justify-center gap-2">
              {projects.map((p, i) => (
                <button
                  key={p.key}
                  type="button"
                  aria-label={`Go to project ${i + 1}`}
                  onClick={() => setActive(i)}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    i === active ? "w-7 bg-gold" : "w-2 bg-border hover:bg-gold/50"
                  }`}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
